class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.availabilityRun = 0;
    this.recommendationRun = 0;
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.addEventListener('click', (event) => {
      const button = event.target.closest('[data-cart-recommendation-add]');
      if (!button) return;
      event.preventDefault();
      this.addRecommendation(button);
    });
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    if (
      typeof subscribe === 'function' &&
      typeof PUB_SUB_EVENTS !== 'undefined' &&
      !this.cartUpdateUnsubscriber
    ) {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        setTimeout(() => this.refreshEnhancements());
      });
    }
    if (!this.availabilityObserver) {
      this.availabilityObserver = new MutationObserver((mutations) => {
        const cartMarkupChanged = mutations.some((mutation) =>
          Array.from(mutation.addedNodes).some(
            (node) =>
              node.nodeType === Node.ELEMENT_NODE &&
              (node.matches('cart-drawer-items, .drawer__footer, [data-cart-availability-item]') ||
                node.querySelector?.('[data-cart-availability-item]'))
          )
        );
        if (cartMarkupChanged) setTimeout(() => this.refreshEnhancements());
      });
      this.availabilityObserver.observe(this, { childList: true, subtree: true });
    }
    this.refreshEnhancements();
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
    this.cartUpdateUnsubscriber = undefined;
    if (this.availabilityObserver) this.availabilityObserver.disconnect();
    this.availabilityObserver = undefined;
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.refreshEnhancements();
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  formatFriendlyDate(dateValue) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateValue || '')) return '';
    const parts = dateValue.split('-');
    const day = Number(parts[2]);
    const month = Number(parts[1]);
    const months = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];
    const suffix = day >= 11 && day <= 13 ? 'th' : ['th', 'st', 'nd', 'rd'][day % 10] || 'th';
    return `${day}${suffix} ${months[month - 1]}`;
  }

  fetchAvailability(sku, quantity) {
    const endpoint = this.dataset.availabilityEndpoint;
    if (!endpoint || !sku) return Promise.resolve(null);

    window.maltashopperCartAvailabilityCache = window.maltashopperCartAvailabilityCache || new Map();
    const cache = window.maltashopperCartAvailabilityCache;
    const key = `${sku}:${quantity}`;
    if (!cache.has(key)) {
      const separator = endpoint.includes('?') ? '&' : '?';
      const request = fetch(
        `${endpoint}${separator}sku=${encodeURIComponent(sku)}&qty=${encodeURIComponent(quantity)}`,
        { credentials: 'omit' }
      )
        .then((response) => {
          if (!response.ok) throw new Error('availability request failed');
          return response.json();
        })
        .catch((error) => {
          cache.delete(key);
          throw error;
        });
      cache.set(key, request);
    }
    return cache.get(key);
  }

  setItemAvailability(item, text, state, fulfilmentGroup) {
    const availability = item.querySelector('[data-cart-availability]');
    const label = item.querySelector('[data-cart-availability-text]');
    if (!availability || !label) return;

    availability.classList.remove('is-checking', 'is-available', 'is-neutral', 'is-unavailable');
    availability.classList.add(`is-${state}`);
    label.textContent = text;
    item.dataset.cartFulfilmentGroup = fulfilmentGroup;
  }

  refreshItemAvailability(item, runId) {
    if (item.dataset.cartVendor !== 'ikea') return Promise.resolve();
    if (item.dataset.cartLiveIkea !== 'true') return Promise.resolve();

    const sku = item.dataset.cartSku;
    const quantity = Number(item.dataset.cartQuantity || 1);
    if (!sku) {
      this.setItemAvailability(item, this.dataset.unavailableLabel, 'neutral', 'ikea-unknown');
      return Promise.resolve();
    }

    this.setItemAvailability(item, this.dataset.checkingLabel, 'checking', 'ikea-checking');
    return this.fetchAvailability(sku, quantity)
      .then((data) => {
        if (runId !== this.availabilityRun || !data) return;
        const cataniaStock = data.status === 'HIGH_IN_STOCK' || data.status === 'LOW_IN_STOCK';

        if (cataniaStock) {
          const eta = this.formatFriendlyDate(data.eta_date);
          const label = eta ? `${this.dataset.etaPrefix} ${eta}` : 'Available via IKEA Catania';
          this.setItemAvailability(item, label, 'available', 'ikea-catania');
          return;
        }
        if (data.piacenza_available) {
          this.setItemAvailability(item, this.dataset.piacenzaLabel, 'available', 'ikea-piacenza');
          return;
        }
        if (data.order_in_store_only || data.home_delivery) {
          this.setItemAvailability(item, this.dataset.orderStoreLabel, 'available', 'ikea-supplier');
          return;
        }
        this.setItemAvailability(item, this.dataset.unavailableLabel, 'unavailable', 'ikea-unavailable');
      })
      .catch(() => {
        if (runId !== this.availabilityRun) return;
        this.setItemAvailability(item, this.dataset.errorLabel, 'neutral', 'ikea-unknown');
      });
  }

  updateFulfilmentSummary(items) {
    const summary = this.querySelector('[data-cart-fulfilment-summary]');
    const summaryText = this.querySelector('[data-cart-fulfilment-summary-text]');
    if (!summary || !summaryText) return;

    const groups = new Set(items.map((item) => item.dataset.cartFulfilmentGroup).filter(Boolean));
    const showSummary = items.length > 1 && groups.size > 1;
    summary.hidden = !showSummary;
    summaryText.textContent = showSummary ? this.dataset.mixedLabel : '';
  }

  refreshAvailability() {
    const items = Array.from(this.querySelectorAll('[data-cart-availability-item]'));
    const runId = ++this.availabilityRun;
    this.updateFulfilmentSummary(items);
    Promise.allSettled(items.map((item) => this.refreshItemAvailability(item, runId))).then(() => {
      if (runId === this.availabilityRun) this.updateFulfilmentSummary(items);
    });
  }

  refreshEnhancements() {
    this.refreshAvailability();
    this.refreshRecommendations();
  }

  refreshRecommendations() {
    const container = this.querySelector('[data-cart-recommendations]');
    if (!container) return Promise.resolve();

    const productId = container.dataset.productId;
    const sectionId = container.dataset.sectionId;
    if (!productId || !sectionId) {
      container.hidden = true;
      container.innerHTML = '';
      return Promise.resolve();
    }

    const lineKeys = Array.from(this.querySelectorAll('[data-cart-line-key]'))
      .map((item) => item.dataset.cartLineKey)
      .filter(Boolean)
      .sort()
      .join(',');
    const cacheKey = `${productId}:${lineKeys}`;
    const runId = ++this.recommendationRun;
    const shopRoot = window.Shopify?.routes?.root || '/';
    const requestUrl = `${shopRoot}recommendations/products?section_id=${encodeURIComponent(
      sectionId
    )}&product_id=${encodeURIComponent(productId)}&limit=6&intent=related`;

    window.maltashopperCartRecommendationsCache = window.maltashopperCartRecommendationsCache || new Map();
    const cache = window.maltashopperCartRecommendationsCache;
    if (!cache.has(cacheKey)) {
      const request = fetch(requestUrl, { credentials: 'same-origin' })
        .then((response) => {
          if (!response.ok) throw new Error('recommendations request failed');
          return response.text();
        })
        .catch((error) => {
          cache.delete(cacheKey);
          throw error;
        });
      cache.set(cacheKey, request);
    }

    container.setAttribute('aria-busy', 'true');
    return cache
      .get(cacheKey)
      .then((html) => {
        if (runId !== this.recommendationRun || !container.isConnected) return;
        const content = new DOMParser()
          .parseFromString(html, 'text/html')
          .querySelector('[data-cart-recommendations-content]');
        const recommendationMarkup = content?.innerHTML.trim() || '';
        container.innerHTML = recommendationMarkup;
        container.hidden = !recommendationMarkup;
      })
      .catch(() => {
        if (runId !== this.recommendationRun || !container.isConnected) return;
        container.hidden = true;
        container.innerHTML = '';
      })
      .finally(() => {
        if (runId === this.recommendationRun && container.isConnected) {
          container.removeAttribute('aria-busy');
        }
      });
  }

  async addRecommendation(button) {
    if (button.disabled) return;

    const variantId = button.dataset.cartRecommendationAdd;
    const addLabel = button.dataset.addLabel || 'Add';
    const loadingLabel = button.dataset.loadingLabel || 'Adding...';
    const errorContainer = this.querySelector('#CartDrawer-CartErrors');
    if (!variantId) return;

    button.disabled = true;
    button.textContent = loadingLabel;
    if (errorContainer) errorContainer.textContent = '';

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    formData.append('sections', this.getSectionsToRender().map((section) => section.id).join(','));
    formData.append('sections_url', window.location.pathname);

    try {
      const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData,
      });
      const state = await response.json();
      if (!response.ok || state.status) throw new Error(state.description || 'Could not add this product');
      this.classList.remove('is-empty');
      this.renderContents(state);
    } catch (error) {
      if (errorContainer) errorContainer.textContent = error.message;
      if (button.isConnected) {
        button.disabled = false;
        button.textContent = addLabel;
      }
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.refreshEnhancements();
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
